/*
 * Naturlabs wave 2: naming line showcase helpers (sections/nl-home-lines.liquid, styles in nl-w2-lines.css)
 *
 *   <nl-w2-rail-progress data-track="TrackId">
 *     [data-progress-current]   counter text (01 .. total), follows the scroll position
 *     [data-progress-thumb]     thumb of the hairline bar: width = visible share, position = scroll progress
 *   Pointer spotlight: .nl-w2-line tiles get --spot-x / --spot-y (px) in one rAF per frame (fine pointers only,
 *   never with reduced motion). Theme editor: selecting a line block scrolls the rail to it.
 * Transform only, passive listeners, no layout reads inside scroll handlers apart from the rAF batch.
 */
(() => {
  'use strict';

  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduced = () =>
    window.NL && window.NL.motion ? window.NL.motion.reduced : mqReduced.matches;

  /* ------------------------------------------------------------------
   * <nl-w2-rail-progress>
   * ------------------------------------------------------------------ */
  class RailProgress extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.track = document.getElementById(this.dataset.track || '');
      this.thumb = this.querySelector('[data-progress-thumb]');
      this.current = this.querySelector('[data-progress-current]');
      if (!this.track || !this.thumb) return;
      this.bound = true;
      this.measure = true;
      this.onScroll = () => this.queue(false);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queue(true));
        this.resizeObserver.observe(this.track);
      }
      this.queue(true);
    }

    disconnectedCallback() {
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.bound = false;
    }

    queue(measure) {
      if (measure) this.measure = true;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.render();
      });
    }

    render() {
      const track = this.track;
      const total = track.children.length;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const share = track.scrollWidth > 0 ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
      const progress = max > 1 ? Math.min(1, Math.max(0, Math.abs(track.scrollLeft) / max)) : 0;

      if (this.measure) {
        this.measure = false;
        this.thumb.style.width = `${(share * 100).toFixed(3)}%`;
      }

      // translateX percentages are relative to the thumb itself: travel = (1 - share) / share thumb widths
      const travel = share < 1 ? ((1 - share) / share) * progress : 0;
      this.thumb.style.transform = `translate3d(${(travel * 100).toFixed(3)}%, 0, 0)`;

      if (this.current && total > 0) {
        const index = Math.round(progress * (total - 1)) + 1;
        const text = String(index).padStart(2, '0');
        if (this.current.textContent !== text) this.current.textContent = text;
      }
    }
  }

  if (!customElements.get('nl-w2-rail-progress')) customElements.define('nl-w2-rail-progress', RailProgress);

  /* ------------------------------------------------------------------
   * Pointer spotlight on tiles (bound once per document)
   * ------------------------------------------------------------------ */
  if (!window.__nlW2LinesSpot) {
    window.__nlW2LinesSpot = true;

    let frame = null;
    let target = null;
    let x = 0;
    let y = 0;

    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse' || !mqFine.matches || reduced()) return;
        const tile = event.target instanceof Element ? event.target.closest('.nl-w2-line') : null;
        if (!tile) return;
        const rect = tile.getBoundingClientRect();
        target = tile;
        x = event.clientX - rect.left;
        y = event.clientY - rect.top;
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          if (!target) return;
          target.style.setProperty('--spot-x', `${x.toFixed(1)}px`);
          target.style.setProperty('--spot-y', `${y.toFixed(1)}px`);
        });
      },
      { passive: true }
    );

    /* Theme editor: bring the selected line into view inside the rail */
    document.addEventListener('shopify:block:select', (event) => {
      const item = event.target;
      if (!(item instanceof Element) || !item.closest('.nl-w2-lines__track')) return;
      const track = item.closest('.nl-w2-lines__track');
      const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      const offset = item.offsetLeft - track.offsetLeft - padding;
      track.scrollTo({ left: Math.max(0, offset), behavior: reduced() ? 'auto' : 'smooth' });
    });
  }
})();
